import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { clearAuth, getToken } from '@/utils/auth'

/** go-zero 在 httpx.Parse 失败时可能返回纯文本 body，而非 { message } JSON */
function extractBackendMessage(data) {
  if (data == null) return ''
  if (typeof data === 'string') return data
  if (typeof data === 'object') {
    return data.message || data.msg || data.error || ''
  }
  return String(data)
}

function isLoginRequest(config) {
  const url = String(config?.url || '')
  const method = String(config?.method || '').toLowerCase()
  return method === 'post' && url.includes('/api/v1/login')
}

function isTimeoutError(error) {
  return error?.code === 'ECONNABORTED' || String(error?.message || '').toLowerCase().includes('timeout')
}

let forbiddenAuthCheckPromise = null

function redirectToLogin() {
  clearAuth()
  if (router?.currentRoute?.value?.path !== '/login') {
    const redirect = router?.currentRoute?.value?.fullPath || '/'
    router.push(`/login?redirect=${encodeURIComponent(redirect)}`)
  }
}

async function isCurrentSessionStillValid() {
  const token = getToken()
  if (!token) return false
  if (!forbiddenAuthCheckPromise) {
    forbiddenAuthCheckPromise = axios.get(`${resolvedBaseURL}/api/v1/user/me`, {
      timeout: 5000,
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => true).catch(() => false).finally(() => {
      forbiddenAuthCheckPromise = null
    })
  }
  return forbiddenAuthCheckPromise
}

// 创建 axios 实例
// 规则：
// 1) 优先使用环境变量 VUE_APP_BASE_API（便于测试/生产覆盖）
// 2) 开发环境未配置时，默认直连后端 8888，避免请求误打到前端 8080
// 3) 非开发环境未配置时，保持相对路径兼容现有部署
const resolvedBaseURL = process.env.VUE_APP_BASE_API
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : '')

const service = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 10000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Request Error:', error)
    ElMessage.error('请求发送失败')
    return Promise.reject(error)
  }
)

// 响应拦截器
// 后端 go-zero 直接返回裸 JSON（无 {code, data} 包装层），统一透传 response.data
service.interceptors.response.use(
  response => {
    return response.data
  },
  async error => {
    // 允许在单次请求上通过 config.silent=true 关闭全局错误弹窗（如批量统计）
    // 以及全局忽略 axios 的取消错误（ERR_CANCELED），这类通常是路由跳转/组件卸载导致的正常中断
    if ((error?.config && error.config.silent) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    console.error('网络错误:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    })

    let userMessage = ''

    if (isTimeoutError(error)) {
      userMessage = '请求超时，请稍后重试'
      ElMessage.error(userMessage)
    } else if (error.message === 'Network Error') {
      userMessage = '网络连接失败，请检查后端服务是否运行（端口 8888）'
      ElMessage.error(userMessage)
    } else if (error.response) {
      switch (error.response.status) {
        case 400:
          userMessage = isLoginRequest(error?.config)
            ? (extractBackendMessage(error.response.data) || '登录信息填写有误')
            : '请求参数错误：' + extractBackendMessage(error.response.data)
          ElMessage.error(userMessage)
          break
        case 401:
          if (isLoginRequest(error?.config)) {
            userMessage = '账号或密码错误，请重试'
            ElMessage.error(userMessage)
            break
          }
          userMessage = '登录状态已过期，请重新登录'
          ElMessage.error(userMessage)
          redirectToLogin()
          break
        case 403:
          if (!(await isCurrentSessionStillValid())) {
            userMessage = '登录状态已失效，请重新登录'
            ElMessage.error(userMessage)
            redirectToLogin()
          } else {
            userMessage = '当前账号无权限访问，请联系管理员开通权限'
            ElMessage.error(userMessage)
          }
          break
        case 429:
          userMessage = isLoginRequest(error?.config)
            ? '登录尝试过于频繁，请等待 10 分钟后再试'
            : '操作过于频繁，请稍后再试'
          ElMessage.error(userMessage)
          break
        case 404:
          userMessage = extractBackendMessage(error.response.data) || ('接口不存在：' + error.config?.url)
          ElMessage.error(userMessage)
          break
        case 500:
          userMessage = '服务器内部错误：' + extractBackendMessage(error.response.data)
          ElMessage.error(userMessage)
          break
        default:
          userMessage = `连接错误 ${error.response.status}`
          ElMessage.error(userMessage)
      }
    } else if (error.request) {
      userMessage = '服务器无响应，请检查服务器状态'
      ElMessage.error(userMessage)
    } else if (error.message?.includes('timeout')) {
      userMessage = '请求超时'
      ElMessage.error(userMessage)
    } else {
      userMessage = '未知网络错误'
      ElMessage.error(userMessage)
    }

    // 统一覆盖原始 axios message，避免页面显示 "Request failed with status code xxx"
    // 注意：某些环境下 AxiosError.message 可能是只读 getter，直接赋值会抛错
    if (userMessage) {
      try {
        error.message = userMessage
      } catch (_) {
        // 保底：挂在自定义字段上，避免错误处理自身崩溃
        error._userMessage = userMessage
      }
      error._userNotified = true
    }

    return Promise.reject(error)
  }
)

export default service
